import React, { useState, useEffect } from 'react';
import { getDashboardStats, seedData } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
    ShoppingCart,
    CurrencyDollar,
    Users,
    TrendUp,
    CalendarBlank,
    ArrowRight,
    Plus,
    Receipt,
    Package,
    ChartLine
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

const DashboardPage = () => {
    const { user, can } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    const isSeller = user?.role === 'seller';
    // Label adaptativo: para promotoras usamos "activación/activaciones"
    const L = isSeller
        ? { sale: 'activación', sales: 'activaciones', salesCap: 'Activaciones', newSale: 'Nueva activación', pos: 'punto de activación', certificates: 'activaciones' }
        : { sale: 'venta', sales: 'ventas', salesCap: 'Ventas', newSale: 'Nueva venta', pos: 'punto de venta', certificates: 'certificados' };

    // Gates RBAC para los bloques opcionales del dashboard
    const canSeeRevenue = can('dashboard.view_revenue');
    const canSeeQuickActions = can('dashboard.quick_actions');

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            const response = await getDashboardStats();
            setStats(response.data);
        } catch (error) {
            console.error('Error fetching stats:', error);
            if (error.response?.status === 401) return;
            try {
                await seedData();
                const response = await getDashboardStats();
                setStats(response.data);
            } catch (seedError) {
                console.error('Error seeding data:', seedError);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSeed = async () => {
        try {
            await seedData();
            toast.success('Datos de prueba creados');
            fetchStats();
        } catch (error) {
            if (error.response?.data?.message === 'Already seeded') {
                toast.info('Los datos ya fueron creados');
            } else {
                toast.error('Error al crear datos');
            }
        }
    };

    const formatCurrency = (amount) => new Intl.NumberFormat('es-MX', {
        style: 'currency', currency: 'MXN', minimumFractionDigits: 0
    }).format(amount || 0);

    const greeting = () => {
        const h = new Date().getHours();
        if (h < 12) return 'Buenos días';
        if (h < 19) return 'Buenas tardes';
        return 'Buenas noches';
    };

    const today = new Date().toLocaleDateString('es-MX', {
        timeZone: 'America/Mexico_City',
        weekday: 'long', day: 'numeric', month: 'long'
    });
    // Capitalize only first letter (avoid "Miércoles, 6 De Mayo")
    const todayFormatted = today.charAt(0).toUpperCase() + today.slice(1);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#1F2D4B] border-t-transparent"></div>
            </div>
        );
    }

    const Metric = ({ label, value, change, changeType = 'up', context, icon: Icon, onClick, testid }) => (
        <div
            className={`group relative bg-white border border-[#ECE3D2] rounded-lg p-5 transition-all ${onClick ? 'cursor-pointer hover:border-[#1F2D4B]/30 hover:shadow-sm' : ''}`}
            onClick={onClick}
            data-testid={testid}
        >
            <div className="flex items-start justify-between mb-3">
                <span className="text-[11px] font-semibold text-[#8B8474] uppercase tracking-wider">
                    {label}
                </span>
                {Icon && (
                    <div className="w-7 h-7 rounded-md bg-[#F8F4EC] flex items-center justify-center text-[#4B5878] group-hover:bg-[#1F2D4B] group-hover:text-white transition-colors">
                        <Icon size={14} weight="bold" />
                    </div>
                )}
            </div>
            <div className="flex items-baseline gap-2">
                <span className="font-display text-3xl font-bold text-[#1F2D4B] tracking-tight">
                    {value}
                </span>
                {change && (
                    <span className={`status-badge ${changeType === 'up' ? 'status-paid' : changeType === 'down' ? 'status-failed' : 'status-pending'}`}>
                        {change}
                    </span>
                )}
            </div>
            {context && (
                <p className="text-[12px] text-[#8B8474] mt-1.5">{context}</p>
            )}
        </div>
    );

    return (
        <div className="space-y-8 animate-fade-up max-w-[1400px]" data-testid="dashboard-page">
            {/* Breadcrumb + actions */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs">
                    <span className="text-[#8B8474]">Dashboard</span>
                    <span className="text-[#C7BCA5]">/</span>
                    <span className="text-[#1F2D4B] font-medium">Vista general</span>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        onClick={() => navigate('/reports')}
                        variant="outline"
                        className="h-10 text-sm rounded-md border-[#DCD1BC] bg-white hover:bg-[#FAF6F0] text-[#1F2D4B] font-medium"
                        data-testid="view-reports-btn"
                    >
                        <ChartLine size={14} weight="bold" className="mr-1.5" />
                        Ver reportes
                    </Button>
                    <Button
                        onClick={() => navigate('/pos')}
                        className="h-10 px-4 text-sm rounded-md bg-[#1F2D4B] hover:bg-[#2E3F60] text-white font-medium btn-lift"
                        data-testid="new-sale-btn"
                    >
                        <Plus size={14} weight="bold" className="mr-1.5" />
                        {L.newSale}
                    </Button>
                </div>
            </div>

            {/* Hero greeting */}
            <div className="flex items-end justify-between pb-6 border-b border-[#ECE3D2]">
                <div>
                    <p className="text-xs text-[#8B8474]">{todayFormatted}</p>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#1F2D4B] mt-2 leading-tight tracking-tight">
                        {greeting()}, {user?.name?.split(' ')[0] || 'Alejandro'}
                    </h1>
                    <p className="text-sm text-[#4B5878] mt-2 max-w-xl">
                        Aquí tienes el resumen de actividad en tiempo real.
                        Generaste <span className="text-[#1F2D4B] font-semibold">{stats?.today?.count || 0}</span> {L.sales} hoy.
                    </p>
                </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <Metric
                    label={isSeller ? 'Mis activaciones hoy' : 'Ventas de hoy'}
                    value={stats?.today?.count || 0}
                    context={(canSeeRevenue ? formatCurrency(stats?.today?.amount) : `${stats?.today?.certificates || 0} ${L.certificates}`) + (stats?.today?.courtesies_count ? ` · +${stats.today.courtesies_count} cortesía(s)` : '')}
                    icon={ShoppingCart}
                    onClick={() => navigate(isSeller ? '/my-sales?range=today' : '/reports?range=today')}
                    testid="kpi-today-sales"
                />
                {isSeller && stats?.week && (
                    <Metric
                        label="Esta semana (Lun–Dom)"
                        value={stats?.week?.count || 0}
                        context={(canSeeRevenue ? formatCurrency(stats?.week?.amount) + ' · ' : '') + `${stats?.week?.certificates || 0} ${L.certificates}`}
                        icon={CalendarBlank}
                        onClick={() => navigate('/my-sales?range=current_cut')}
                        testid="kpi-week-sales"
                    />
                )}
                <Metric
                    label={isSeller ? 'Mi mes' : 'Este mes'}
                    value={stats?.month?.count || 0}
                    context={(canSeeRevenue ? formatCurrency(stats?.month?.amount) + ' · ' : '') + `${stats?.month?.certificates || 0} ${L.certificates}`}
                    icon={CalendarBlank}
                    onClick={isSeller ? undefined : () => navigate('/reports')}
                    testid="kpi-month-sales"
                />
                {!isSeller && (
                    <Metric
                        label="Total general"
                        value={stats?.total?.count || 0}
                        context={canSeeRevenue ? formatCurrency(stats?.total?.amount) : `${stats?.total?.certificates || 0} certificados`}
                        icon={TrendUp}
                        testid="kpi-total-sales"
                    />
                )}
                <Metric
                    label="Cortesías del mes"
                    value={stats?.month?.courtesies_count || 0}
                    context={`${stats?.month?.courtesies_certificates || 0} certificados otorgados`}
                    icon={Users}
                    testid="kpi-courtesies-month"
                />
                {!isSeller && (
                    <Metric
                        label="Vendedores activos"
                        value={stats?.active_sellers_today || 0}
                        context="Registrados hoy"
                        icon={Users}
                        onClick={() => navigate('/attendance')}
                        testid="kpi-active-sellers"
                    />
                )}
            </div>

            {/* Two columns — gated por permisos dashboard.view_revenue / quick_actions */}
            {(canSeeRevenue || canSeeQuickActions) && (
            <div className={`grid grid-cols-1 ${canSeeRevenue && canSeeQuickActions ? 'lg:grid-cols-3' : 'lg:grid-cols-1'} gap-4`}>
                {/* Month income card */}
                {canSeeRevenue && (
                <div className={`${canSeeQuickActions ? 'lg:col-span-2' : ''} bg-white border border-[#ECE3D2] rounded-lg p-6`} data-testid="month-revenue-card">
                    <div className="flex items-start justify-between mb-6">
                        <div>
                            <h2 className="font-display text-base font-bold text-[#1F2D4B]">Ingresos del mes</h2>
                            <p className="text-xs text-[#8B8474] mt-0.5">Acumulado del periodo actual</p>
                        </div>
                        <span className="text-[11px] font-medium text-[#4B5878] bg-[#F8F4EC] px-2 py-1 rounded">
                            {new Date().toLocaleDateString('es-MX', { timeZone: 'America/Mexico_City', month: 'long' })}
                        </span>
                    </div>

                    <div className="flex items-baseline gap-3 mb-8">
                        <span className="font-display text-5xl font-bold text-[#1F2D4B] tracking-tight">
                            {formatCurrency(stats?.month?.amount)}
                        </span>
                        <span className="text-sm text-[#4B5878]">
                            · {stats?.month?.count || 0} certificados
                        </span>
                    </div>

                    <div className="grid grid-cols-2 gap-4 pt-6 border-t border-[#ECE3D2]">
                        <div>
                            <div className="text-[11px] font-semibold text-[#8B8474] uppercase tracking-wider mb-1">
                                Ticket promedio
                            </div>
                            <div className="font-display text-xl font-bold text-[#1F2D4B]">
                                {formatCurrency(
                                    stats?.month?.count
                                        ? stats.month.amount / stats.month.count
                                        : 0
                                )}
                            </div>
                        </div>
                        <div>
                            <div className="text-[11px] font-semibold text-[#8B8474] uppercase tracking-wider mb-1">
                                Total histórico
                            </div>
                            <div className="font-display text-xl font-bold text-[#1F2D4B]">
                                {formatCurrency(stats?.total?.amount)}
                            </div>
                        </div>
                    </div>
                </div>
                )}

                {/* Quick actions */}
                {canSeeQuickActions && (
                <div className="bg-white border border-[#ECE3D2] rounded-lg p-6" data-testid="quick-actions-card">
                    <h2 className="font-display text-base font-bold text-[#1F2D4B]">Acciones rápidas</h2>
                    <p className="text-xs text-[#8B8474] mt-0.5 mb-5">Tareas frecuentes</p>

                    <div className="space-y-2">
                        <button
                            onClick={() => navigate('/pos')}
                            data-testid="quick-new-sale-btn"
                            className="group w-full flex items-center justify-between p-3 rounded-md border border-[#ECE3D2] hover:border-[#1F2D4B]/30 hover:bg-[#FAF6F0] transition-all text-left"
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-md bg-[#1F2D4B] text-white flex items-center justify-center">
                                    <ShoppingCart size={14} weight="bold" />
                                </div>
                                <div>
                                    <div className="text-sm font-medium text-[#1F2D4B]">Nueva venta</div>
                                    <div className="text-[11px] text-[#8B8474]">Ir al punto de venta</div>
                                </div>
                            </div>
                            <ArrowRight size={14} className="text-[#8B8474] group-hover:text-[#1F2D4B] transition-colors" />
                        </button>

                        <button
                            onClick={() => navigate('/sales')}
                            data-testid="quick-view-sales-btn"
                            className="group w-full flex items-center justify-between p-3 rounded-md border border-[#ECE3D2] hover:border-[#1F2D4B]/30 hover:bg-[#FAF6F0] transition-all text-left"
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-md bg-[#F8F4EC] text-[#1F2D4B] flex items-center justify-center">
                                    <Receipt size={14} weight="bold" />
                                </div>
                                <div>
                                    <div className="text-sm font-medium text-[#1F2D4B]">Historial</div>
                                    <div className="text-[11px] text-[#8B8474]">Ver ventas anteriores</div>
                                </div>
                            </div>
                            <ArrowRight size={14} className="text-[#8B8474] group-hover:text-[#1F2D4B] transition-colors" />
                        </button>

                        {(user?.role === 'admin' || user?.role === 'manager') && (
                            <button
                                onClick={() => navigate('/products')}
                                data-testid="quick-products-btn"
                                className="group w-full flex items-center justify-between p-3 rounded-md border border-[#ECE3D2] hover:border-[#1F2D4B]/30 hover:bg-[#FAF6F0] transition-all text-left"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-md bg-[#F8F4EC] text-[#1F2D4B] flex items-center justify-center">
                                        <Package size={14} weight="bold" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-medium text-[#1F2D4B]">Productos</div>
                                        <div className="text-[11px] text-[#8B8474]">Catálogo y certificados</div>
                                    </div>
                                </div>
                                <ArrowRight size={14} className="text-[#8B8474] group-hover:text-[#1F2D4B] transition-colors" />
                            </button>
                        )}
                    </div>
                </div>
                )}
            </div>
            )}

            {/* Dev helper */}
            {process.env.NODE_ENV === 'development' && (
                <div>
                    <Button
                        onClick={handleSeed}
                        variant="outline"
                        className="text-xs h-8 border-[#DCD1BC]"
                    >
                        Cargar datos de prueba
                    </Button>
                </div>
            )}
        </div>
    );
};

export default DashboardPage;
